// Option thumbnails for the design controls — each one draws its own effect
// rather than labelling it, so a control is legible before it's understood.
//
// Extracted from ShapeSelect when the mobile customiser needed the same
// drawings (docs/design handoff: "port them as-is"). ShapeSelect's originals
// are re-exported here unchanged, so the desktop pickers render pixel for pixel
// what they did before.
//
// Everything draws in `currentColor` so a thumbnail stays visible on any
// surface — coal on light, cloud on dark, and correct inside the mobile sheet
// without a second palette.

const DEFAULT_CLASS = "w-9 h-9 text-coal dark:text-cloud"

// Fluid joins the cells: an L of two pills (square where they meet, a fillet
// in the inside corner) and one lone dot, the three things the shape does.
const FLUID_CELL = 26 / 3
const FLUID_R = FLUID_CELL / 2
const FLUID_IN = 3 + FLUID_CELL

function FluidThumb({ className }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      <rect x="3" y="3" width="26" height={FLUID_CELL} rx={FLUID_R} fill="currentColor" />
      <rect x="3" y="3" width={FLUID_CELL} height="26" rx={FLUID_R} fill="currentColor" />
      <path d={`M${FLUID_IN} ${FLUID_IN}h${FLUID_R}a${FLUID_R} ${FLUID_R} 0 0 0 -${FLUID_R} ${FLUID_R}z`} fill="currentColor" />
      <circle cx={29 - FLUID_R} cy={29 - FLUID_R} r={FLUID_R} fill="currentColor" />
    </svg>
  )
}

export function ModuleThumb({ shape, className = DEFAULT_CLASS }) {
  if (shape === "fluid") return <FluidThumb className={className} />
  const cells = [
    [ 3, 3 ], [ 12, 3 ], [ 21, 3 ],
    [ 3, 12 ], [ 12, 12 ], [ 21, 12 ],
    [ 3, 21 ], [ 12, 21 ], [ 21, 21 ]
  ]
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      {cells.map(([ x, y ], i) =>
        shape === "dot"
          ? <circle key={i} cx={x + 4} cy={y + 4} r="4" fill="currentColor" />
          : <rect key={i} x={x} y={y} width="8" height="8" rx={shape === "rounded" ? 2.5 : 0} fill="currentColor" />
      )}
    </svg>
  )
}

// A single finder pattern (outer ring + centre) drawn in the given eye shape.
export function EyeThumb({ shape, className = DEFAULT_CLASS }) {
  if (shape === "circle") {
    return (
      <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
        <circle cx="16" cy="16" r="13" fill="none" stroke="currentColor" strokeWidth="4" />
        <circle cx="16" cy="16" r="5" fill="currentColor" />
      </svg>
    )
  }
  // Leaf squares the top-left + bottom-right corners; an SVG rect can't do
  // per-corner radii, so approximate with a path.
  if (shape === "leaf") {
    return (
      <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
        <path d="M3 3 H23 A8 8 0 0 1 29 9 V29 H9 A8 8 0 0 1 3 23 Z" fill="none" stroke="currentColor" strokeWidth="4" />
        <path d="M12 12 H18 A4 4 0 0 1 21 15 V21 H15 A4 4 0 0 1 12 18 Z" fill="currentColor" />
      </svg>
    )
  }
  const rx = shape === "rounded" ? 6 : 0
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      <rect x="3" y="3" width="26" height="26" rx={rx} fill="none" stroke="currentColor" strokeWidth="4" />
      <rect x="12" y="12" width="8" height="8" rx={rx ? 2 : 0} fill="currentColor" />
    </svg>
  )
}

// Theme-aware frame thumbnail. FrameSelect keeps its own copy, which hardcodes
// #fff / #111 / #E4E4E7 and therefore renders a white card on a dark surface —
// fine where it sits today (a light panel) but wrong inside the mobile sheet.
// This version paints from the current surface tokens instead. The two should
// be reconciled when the desktop picker's dark mode is next looked at; they are
// deliberately not reconciled here, because this handoff leaves desktop alone.
export function FrameThumb({ frame, className = "w-[34px] h-[34px] text-coal dark:text-cloud" }) {
  if (frame === "rounded") {
    return (
      <svg viewBox="0 0 40 40" className={className} aria-hidden="true">
        <rect x="2" y="2" width="36" height="36" rx="7" fill="var(--bg-surface)" stroke="currentColor" strokeWidth="1.5" />
        <rect x="11" y="11" width="18" height="18" fill="currentColor" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 40 40" className={className} aria-hidden="true">
      <rect x="4" y="4" width="32" height="32" fill="var(--bg-surface)" stroke="var(--border-default)" />
      <rect x="11" y="11" width="18" height="18" fill="currentColor" />
    </svg>
  )
}

// Decorative 7x7 code for the colour swatch chips. Cheap on purpose: a real
// render per preset would be six server round-trips to paint six 46px chips.
export function MiniQr({ foreground, background, size = 30 }) {
  const cells = "1110111100010100101101011101011110001011010101110111"
  return (
    <svg width={size} height={size} viewBox="0 0 7 7" aria-hidden="true" className="block rounded-[3px]" style={{ background }}>
      {cells.split("").map((c, i) => (
        c === "1" ? <rect key={i} x={i % 7} y={Math.floor(i / 7)} width="1" height="1" fill={foreground} /> : null
      ))}
    </svg>
  )
}
